import { DocumentStatus } from 'services/documents/entities';
import { URLS } from 'utils/urls';
import type { TransformedNotificationItem } from './EventTypes';
import { isCommentedEvent, isViewedEvent } from './EventTypes';

export type NavigationResult =
  | { type: 'navigate'; pathname: string; search: string }
  | { type: 'skip'; reason: 'noDocumentId' | 'loading' | 'error' | 'noData' };

export const shouldFetchNotificationDocument = (event: TransformedNotificationItem) => {
  return !isViewedEvent(event) && !(isCommentedEvent(event) && Boolean(event.previewUrl));
};

const buildThreadIdSearch = (threadId?: string) => {
  if (!threadId) {
    return '';
  }

  const searchParams = new URLSearchParams({ threadId });
  return `?${searchParams.toString()}`;
};

const isPreviewStatus = (status?: string) => status === DocumentStatus.AWAITING_SIGNATURE || status === DocumentStatus.WON;

type DocumentWithStatus = {
  status?: string;
};

export function resolveNotificationDestination(
  event: TransformedNotificationItem,
  document: DocumentWithStatus | undefined,
  state: { isLoading: boolean; isError: boolean }
): NavigationResult {
  if (!event.documentId) {
    return { type: 'skip', reason: 'noDocumentId' };
  }

  if (isCommentedEvent(event) && event.previewUrl) {
    const url = new URL(event.previewUrl, window.location.origin);

    if (event.threadId) {
      url.searchParams.set('threadId', event.threadId);
    }

    return { type: 'navigate', pathname: url.pathname, search: url.search };
  }

  if (isViewedEvent(event)) {
    return { type: 'navigate', pathname: URLS.documents, search: `?insightsDocId=${event.documentId}` };
  }

  if (state.isLoading) {
    return { type: 'skip', reason: 'loading' };
  }

  if (state.isError) {
    return { type: 'skip', reason: 'error' };
  }

  if (!document) {
    return { type: 'skip', reason: 'noData' };
  }

  const pathname = isPreviewStatus(document.status) ? URLS.previewDocument(event.documentId) : URLS.editDocument(event.documentId);

  if (isCommentedEvent(event)) {
    return { type: 'navigate', pathname, search: buildThreadIdSearch(event.threadId) || '?openComments=true' };
  }

  return { type: 'navigate', pathname, search: '' };
}
